import { useState } from "react";
import { motion } from "framer-motion";
import { Toaster } from "react-hot-toast";
import SalesHeader from "../../components/sales/SalesHeader";
import SalesStats from "../../components/sales/SalesStats";
import SalesTable from "../../components/sales/SalesTable";
import AbandonedCartsTable from "../../components/sales/AbandonedCartsTable";
import Loader from "../../components/common/Loader";
import { useSalesData } from "../../hooks/useSalesData";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { getCountriesList } from "../../const/countryList";

const SalesPage = () => {
  const { orders, filteredOrders, isLoading, activeFilter, activeDateFilter, activeProductFilters, confirmationStatuses, isLoadingStatuses, filterOrders, filterByDate, clearDateFilter, deleteOrder, filterByProductId, removeProductFilter, products, pagination, setPage, setPageSize, activeCountryFilter, filterByCountryCode, refreshOrders } = useSalesData();
  const [activeTab, setActiveTab] = useState<"orders" | "abandoned">("orders");
  const { language } = useAuth();
  const countries = getCountriesList(language);
  const countryOptions = Array.from(
    new Set(
      (orders?.available_countries || filteredOrders?.available_countries || [])
        .map((item) => item.country_code)
        .filter(Boolean) as string[]
    )
  );
  const selectedCountryCode = activeCountryFilter && countryOptions.includes(activeCountryFilter)
    ? activeCountryFilter
    : null;

  if (isLoading && !filteredOrders) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA] px-16 pt-10 pb-16 font-['Plus_Jakarta_Sans']">
      <Toaster position="top-right" />
      <SalesHeader />

      {/* Tabs */}
      <div className="bg-gray-200 rounded-full p-1 flex w-fit mb-6">
        <button
          onClick={() => setActiveTab("orders")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            activeTab === "orders"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {dictionary["orders-tab"]?.[language]}
        </button>
        <button
          onClick={() => setActiveTab("abandoned")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            activeTab === "abandoned"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {dictionary["abandoned-carts-tab"]?.[language]}
        </button>
      </div>

      {activeTab === "orders" && filteredOrders && (
        <>
          <motion.div
            layout
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="mb-4 flex items-center gap-3"
          >
            <label className="text-sm font-medium text-gray-600">País</label>
            <select
              value={selectedCountryCode || ""}
              onChange={(event) => filterByCountryCode(event.target.value || null)}
              className="h-11 min-w-[220px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm outline-none transition focus:border-fluxi focus:ring-2 focus:ring-fluxi/15"
            >
              <option value="">Todos los países</option>
              {countryOptions.map((countryCode) => {
                const countryInfo = countries.find((item) => item.country === countryCode);
                return (
                  <option key={countryCode} value={countryCode}>
                    {countryInfo ? `${countryInfo.flag} ${countryInfo.name}` : countryCode}
                  </option>
                );
              })}
            </select>
          </motion.div>
          <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: "easeOut", layout: { duration: 0.22 } }}
          >
            <SalesStats
              summary={filteredOrders.summary}
              selectedCountryCode={selectedCountryCode}
              orderCount={pagination?.total_items ?? filteredOrders.orders.length}
            />
            <SalesTable
              filterOrders={filterOrders}
              orders={filteredOrders.orders}
              activeFilter={activeFilter}
              onOrderDeleted={deleteOrder}
              onFilterByDate={filterByDate}
              activeDateFilter={activeDateFilter}
              onRemoveDateFilter={clearDateFilter}
              products={products}
              onFilterByProductId={filterByProductId}
              activeProductFilters={activeProductFilters}
              onRemoveProductFilter={removeProductFilter}
              confirmationStatuses={confirmationStatuses}
              isLoadingStatuses={isLoadingStatuses}
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              activeCountryFilter={selectedCountryCode}
              onRefreshOrders={refreshOrders}
            />
          </motion.div>
        </>
      )}

      {activeTab === "abandoned" && <AbandonedCartsTable />}
    </div>
  );
};

export default SalesPage;
